/**
 * The engine's `Telemetry` service. `reportTurnError` is the single error tap
 * (app log, Amplitude, trace). `traceTurnEvent` is the single lifecycle tap
 * (app log, trace). The trace side hands events to main over IPC, where they
 * become spans and span events on the app's OpenTelemetry tracer.
 *
 * Renderer `console.*` never reaches the log file users send to support; only
 * `GlobalLogger` is forwarded to main. Amplitude keeps a count, not a story.
 */
import GlobalLogger from '@shared/utils/logger/GlobalLogger'
import type { ChatTurnTelemetryEvent, TurnErrorEvent } from '@shared/chat-telemetry/turn-events'
import ChatTelemetryIpcRenderer from '@renderer/ipc/ChatTelemetryIpc.renderer'
import { logAmplitudeEvent } from '../utils/amplitude'
import { ChatTurnError, describeTurnError, isRetryable } from './errors'
import type { TurnErrorReportContext } from './services'
import { describeCause, describeTurnErrorForTrace, turnErrorIdentity, type TurnTraceEvent } from './turn-trace'

const LOG_CLASS = 'chat-turn'

export type { TurnErrorReportContext } from './services'

type SubmitMessageErrorType =
  | 'attachment-upload'
  | 'network'
  | 'validation'
  | 'sse-parsing'
  | 'arkose-captcha'
  | 'backend-error'
  | 'credits-exhausted'
  | 'subscription-required'
  | 'card-required'
  | 'onboarding-required'
  | 'generic-error'
  | 'unhandled-exception-error'
  | 'unknown-error'
  | 'unknown'

// Coupled with describeTurnError (errors.ts): both project exhaustively over
// `_tag`, so a new ChatTurnError member must update both switches. The
// Amplitude errorType values are frozen — dashboards depend on them.
function errorTypeForTag(error: Exclude<ChatTurnError, { _tag: 'StaleVerifyDropped' }>): SubmitMessageErrorType {
  switch (error._tag) {
    case 'SseConnectionError':
      return 'network'
    case 'SseDecodeError':
      return 'sse-parsing'
    case 'ArkoseChallengeError':
      return 'arkose-captcha'
    case 'AttachmentUploadError':
      return 'attachment-upload'
    case 'CreditsExhausted':
      // A new value on the frozen enum: it counts refused turns a top-up would
      // have allowed, which no existing bucket separates from server faults.
      return 'credits-exhausted'
    case 'SubscriptionRequired':
      // Also new on the frozen enum: turns a plan pick would have allowed,
      // which the credits bucket would otherwise misreport as a top-up.
      return 'subscription-required'
    case 'CardRequired':
      // Counts turns held back until the account adds a card.
      return 'card-required'
    case 'OnboardingRequired':
      // Turns the onboarding flow would have allowed; counts the accounts the
      // server sent back through it.
      return 'onboarding-required'
    case 'BackendTurnError':
      switch (error.kind) {
        case 'non-json-response':
          return 'generic-error'
        case 'malformed-error-body':
          return 'validation'
        default:
          return 'backend-error'
      }
    case 'UnknownTurnError':
      return 'unhandled-exception-error'
  }
}

function traceLevel(event: TurnTraceEvent): 'info' | 'warn' {
  switch (event.kind) {
    case 'connect:failed':
    case 'stream:reconnect':
    case 'verify:failed':
    case 'challenge:retry':
      return 'warn'
    case 'connect:response':
      return event.status >= 400 ? 'warn' : 'info'
    case 'stream:ended':
      return event.reason === 'done' || event.reason === 'aborted' ? 'info' : 'warn'
    case 'turn:settled':
      return event.outcome === 'failed' ? 'warn' : 'info'
    case 'challenge:result':
      return event.outcome === 'passed' || event.outcome === 'disabled' ? 'info' : 'warn'
    default:
      return 'info'
  }
}

/** Hand an event to main's tracer. Fire-and-forget: a lost event costs a span event, never the turn. */
function recordOnTrace(event: TurnTraceEvent | TurnErrorEvent): void {
  if (typeof __IS_BROWSER__ !== 'undefined' && __IS_BROWSER__) return
  const stamped: ChatTurnTelemetryEvent = { ...event, at: Date.now() }
  void ChatTelemetryIpcRenderer.record(stamped).catch((cause: unknown) => {
    GlobalLogger.warn(`Failed to record ${event.kind} on the trace: ${describeCause(cause)}`, { class: LOG_CLASS })
  })
}

export function traceTurnEvent(event: TurnTraceEvent): void {
  GlobalLogger[traceLevel(event)](JSON.stringify(event), { class: LOG_CLASS })
  recordOnTrace(event)
}

function turnErrorEvent(
  error: Exclude<ChatTurnError, { _tag: 'StaleVerifyDropped' }>,
  turnId: string | undefined,
  rayId: string | undefined,
): TurnErrorEvent {
  const identity = turnErrorIdentity(error)
  return {
    kind: 'turn:error',
    chatId: error.chatId,
    turnId,
    phase: 'phase' in error ? error.phase : undefined,
    ...identity,
    rayId: identity.rayId ?? rayId,
    detail: describeTurnErrorForTrace(error),
    retryable: isRetryable(error),
  }
}

export function reportTurnError(error: ChatTurnError, context: TurnErrorReportContext = {}): void {
  if (error._tag === 'StaleVerifyDropped') {
    GlobalLogger.warn(`StaleVerifyDropped (${error.reason}) chat=${error.chatId}`, { class: LOG_CLASS })
    logAmplitudeEvent('Stale Verify Dropped', { conversationId: error.chatId, reason: error.reason })
    return
  }
  // A signed-out user hitting send is the sign-up flow's business, not a failure.
  if (error._tag === 'BackendTurnError' && error.errorCode === 'SIGNUP_REQUIRED') return

  const rayId = 'rayId' in error ? error.rayId : context.rayId
  GlobalLogger.error(
    `${describeTurnErrorForTrace(error)} chat=${error.chatId}${rayId ? ` ray=${rayId}` : ''}: ${describeTurnError(error)}`,
    { class: LOG_CLASS },
  )

  logAmplitudeEvent('Submit Message Error', {
    errorCode: error._tag === 'BackendTurnError' ? error.errorCode : null,
    errorType: errorTypeForTag(error),
    errorMessage: describeTurnError(error),
    // The user copy collapses most challenge reasons into one string. These
    // keep them apart on the dashboard.
    challengeReason: error._tag === 'ArkoseChallengeError' ? error.reason : undefined,
    challengeDetail: error._tag === 'ArkoseChallengeError' ? error.message : undefined,
    rayId,
    conversationId: error.chatId,
    isNewChat: context.isNewChat ?? false,
    hasAttachments: context.hasAttachments ?? false,
    messageLength: context.messageLength,
    messageRole: context.messageRole ?? null,
  })

  recordOnTrace(turnErrorEvent(error, context.turnId, rayId))
}
